import { capitalize } from '@mui/material';
import { CloudAccountType, Provider, AccountState } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { useDAL } from '@/contexts/useDAL';
import { useEnvironment } from '@/contexts/useEnvironment';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { useUser } from '@/contexts/useUser';

export const URLS: Record<CloudAccountType, string> = {
  [CloudAccountType.Source]:
    'https://eon-public-b2b628cc-1d96-4fda-8dae-c3b1ad3ea03b.s3.amazonaws.com/source-account.yml',
  [CloudAccountType.Vault]:
    'https://eon-public-b2b628cc-1d96-4fda-8dae-c3b1ad3ea03b.s3.amazonaws.com/vault-account.yml',
  [CloudAccountType.Restore]:
    'https://eon-public-b2b628cc-1d96-4fda-8dae-c3b1ad3ea03b.s3.amazonaws.com/restore-account.yml',
  [CloudAccountType.Scanning]:
    'https://eon-public-b2b628cc-1d96-4fda-8dae-c3b1ad3ea03b.s3.amazonaws.com/scanning-account.yml',
  [CloudAccountType.AccountTypeUnspecified]: '',
};

const awsOrganizationURL =
  'https://eon-public-b2b628cc-1d96-4fda-8dae-c3b1ad3ea03b.s3.amazonaws.com/aws-organization.yml';
const awsScanOrganizationAccountsURL =
  'https://eon-public-b2b628cc-1d96-4fda-8dae-c3b1ad3ea03b.s3.amazonaws.com/aws-scan-organization-account-role.yml';

const BASE_URL =
  'https://console.aws.amazon.com/cloudformation/home?#/stacks/create/review?';

export const openUrlInNewWindow = async ({
  url,
  title,
  width,
  height,
}: {
  url: string;
  title: string;
  width: number;
  height: number;
}) => {
  const { screen } = window;
  const left = screen.width / 2 - width / 2;
  const top = screen.height / 2 - height / 2;

  const newWindow = window.open(
    url,
    title,
    `scrollbars=yes,width=${width},height=${height},top=${top},left=${left},screenX=${left},screenY=${top}`
  );
  newWindow?.focus();

  await new Promise<void>((resolve) => {
    const interval = setInterval(() => {
      if (newWindow?.closed) {
        clearInterval(interval);
        resolve();
      }
    }, 500);
  });
};

export const useCreateAccount = (
  accountType: CloudAccountType,
  onDone: (accountId: string) => void,
  initialAccountId = ''
) => {
  const { orgWithoutStackSet } = useFeatureFlags();
  const dal = useDAL();
  const { data } = dal.cloudAccounts.scanning.list();
  const scanningAccountId = data?.accounts?.find(
    (x) => x.cloudProvider === Provider.Aws
  )?.providerAccountId;
  const { user } = useUser();
  const { serviceAccountId } = useEnvironment();
  const [accountId, setAccountId] = useState(initialAccountId);
  const [roleName, setRoleName] = useState(
    `Eon${capitalize(accountType.toLowerCase())}AccountRole`
  );
  const [orgRoleName, setOrgRoleName] = useState(`EonOrganizationAccountRole`);
  const [organizationOUID, setOrganizationOUID] = useState(``);
  const [validationInProgress, setValidationInProgress] = useState(false);
  const [isCreationInProgress, setIsCreationInProgress] = useState(false);
  const [isCreated, setIsCreated] = useState(false);
  const [hasError, setHasError] = useState(false);
  const orgURL = orgWithoutStackSet
    ? awsScanOrganizationAccountsURL
    : awsOrganizationURL;

  const connectOrganization = async () => {
    setValidationInProgress(false);
    setIsCreationInProgress(true);
    const { id, success } = await dal.cloudAccounts.organization
      .connect({
        authParams: {
          roleArn: `arn:aws:iam::${accountId}:role/${orgRoleName}`,
        },
        name: accountId,
        state: AccountState.ACCOUNT_STATE_CONNECTED,
        organizationOUId: organizationOUID,
      })
      .then((res) => ({ success: true, id: res.awsOrganization.id }))
      .catch(() => ({ success: false, id: '' }));
    setIsCreationInProgress(false);
    if (success) {
      setIsCreated(true);
      setTimeout(() => onDone(id), 1000);
    } else {
      setHasError(true);
    }
  };

  const createAccount = async () => {
    setValidationInProgress(false);
    setIsCreationInProgress(true);

    const key = accountType.toLowerCase() as
      | 'source'
      | 'restore'
      | 'vault'
      | 'scanning';

    const { id, success } = await dal.cloudAccounts[key]
      .create({
        cloudProvider: Provider.Aws,
        role: `arn:aws:iam::${accountId}:role/${roleName}`,
        // name: accountId, TODO: allow the user to provide a pretty name and send it here
        regions:
          accountType === CloudAccountType.Restore ||
          accountType === CloudAccountType.Vault
            ? ['us-east-1']
            : undefined,
        isCustomerOwned:
          accountType === CloudAccountType.Vault ||
          accountType === CloudAccountType.Scanning,
      })
      .then((res) => ({ success: true, id: res.id }))
      .catch(() => ({ success: false, id: '' }));
    setIsCreationInProgress(false);
    if (success) {
      setIsCreated(true);
      setTimeout(() => onDone(id), 1000);
    } else {
      setHasError(true);
    }
  };

  const validateAccount = async () => {
    if (!accountId || !roleName || !user || !scanningAccountId) {
      return;
    }

    const searchParams = new URLSearchParams();
    searchParams.append('templateURL', URLS[accountType]);
    searchParams.append(
      'stackName',
      `EonBackup${capitalize(accountType.toLowerCase())}`
    );
    searchParams.append('param_EonAccountId', user.eonAccount.id);
    searchParams.append('param_RoleName', roleName);
    if (accountType === CloudAccountType.Source) {
      searchParams.append('param_ScanningAccountId', scanningAccountId);
    }
    if (serviceAccountId) {
      searchParams.append('param_ServiceAccountId', serviceAccountId);
    }

    setValidationInProgress(true);
    await openUrlInNewWindow({
      url: `${BASE_URL}${searchParams}`,
      title: 'AWSConsole',
      width: window.screen.width * 0.8,
      height: window.screen.height * 0.8,
    });
  };

  const validateOrganization = async () => {
    if (!accountId || !orgRoleName || !user || !scanningAccountId) {
      return;
    }

    const searchParams = new URLSearchParams();
    searchParams.append('templateURL', orgURL);
    searchParams.append('stackName', 'EonOrganization');
    searchParams.append('param_EonAccountId', user.eonAccount.id);
    searchParams.append('param_OrganizationRoleName', orgRoleName);
    searchParams.append('param_OrgId', organizationOUID);
    if (accountType === CloudAccountType.Source) {
      searchParams.append('param_ScanningAccountId', scanningAccountId);
    }
    if (serviceAccountId) {
      searchParams.append('param_ServiceAccountId', serviceAccountId);
    }

    setValidationInProgress(true);
    await openUrlInNewWindow({
      url: `${BASE_URL}${searchParams}`,
      title: 'AWSConsole',
      width: window.screen.width * 0.8,
      height: window.screen.height * 0.8,
    });
  };

  return {
    accountId,
    setAccountId,
    roleName,
    orgRoleName,
    setRoleName,
    setOrgRoleName,
    organizationOUID,
    setOrganizationOUID,
    validationInProgress,
    setValidationInProgress,
    isCreationInProgress,
    isCreated,
    hasError,
    setHasError,
    retry: () => setHasError(false),
    validateAccount,
    validateOrganization,
    createAccount,
    connectOrganization,
  };
};
